import { GraphQLResult } from '@aws-amplify/api-graphql';
import { createAsyncThunk, createSlice, isAnyOf } from '@reduxjs/toolkit';
import {
  FlexCode,
  FlexCodeImport,
  FlexCodeImportByPaidolIdQuery,
  FlexCodesByFlexCodeImportIdQuery,
  FlexCostCode,
  FlexCostCodesByFlexCodeImportIdQuery,
  FlexCostType,
  FlexCostTypesByFlexCodeImportIdQuery,
  FlexGlCode,
  FlexGlCodeByFlexCodeImportIdQuery,
  FlexVendor,
  FlexVendorsByFlexCodeImportIdQuery,
} from 'API';
import { AppDispatch } from 'app/store';
import { RootState } from 'app/store/rootReducer';
import { graphqlOperation } from 'aws-amplify';
import {
  flexCodeImportByPaidolId,
  flexCodesByFlexCodeImportId,
  flexCostCodesByFlexCodeImportId,
  flexCostTypesByFlexCodeImportId,
  flexGlCodeByFlexCodeImportId,
  flexVendorsByFlexCodeImportId,
} from 'graphql/queries';
import type { SearchFlexCodingInput } from 'CDK_API';
import { createDebouncedThunk } from 'util/thunkHelpers';
import sortAlphanumericArrays from 'util/sortAlphanumericArrays';
import { graphqlRequest } from 'app/services/middleware';
// SEE-301 Phase B: all five flex searches now go through the CDK API. The cost-code and
// cost-type pair went last, once their `.rejected` handlers existed — without those, a
// rejection leaves `loading: true` set by `.pending` and the dropdown spins forever.
import { transactionService } from 'app/services/transaction.service';

export const FLEX_PAGE_LIMIT = 25;
export const FLEX_SETTINGS_LIMIT = 1000;

/**
 * Per-coding-type search, for hooks that manage their own local state rather than going
 * through this slice's thunks.
 *
 * SEE-301 Phase B: was a map of `{ query, dataKey }` that callers fed to
 * `graphqlRequest`/`graphqlOperation` and then indexed the response with. Each entry is now
 * the service call itself, so the response arrives already unwrapped and there is no
 * `dataKey` to get wrong.
 *
 * All five go through the CDK API, and so do all seven of this slice's search thunks as of
 * this change — an earlier version of this note said cost codes and cost types were
 * "deliberately still on Amplify", which stopped being true once searchFlexCostCodes and
 * searchFlexCostTypes were repointed. The `graphqlRequest` calls left in this file belong to
 * the flex-code IMPORT thunks, which are a separate family and out of scope here.
 *
 * Consumers of this map hold their own loading state and clear it in a `finally`, so a
 * rejection cannot strand a spinner the way a missing `.rejected` handler in the slice
 * would.
 */
export const FLEX_SEARCH_CONFIGS = {
  glCodes: { search: (input: SearchFlexCodingInput) => transactionService.v2SearchFlexGlCodes(input) },
  projects: { search: (input: SearchFlexCodingInput) => transactionService.v2SearchFlexCodes(input) },
  costCodes: {
    search: (input: SearchFlexCodingInput) => transactionService.v2SearchFlexCostCodes(input),
  },
  costTypes: {
    search: (input: SearchFlexCodingInput) => transactionService.v2SearchFlexCostTypes(input),
  },
  vendors: { search: (input: SearchFlexCodingInput) => transactionService.v2SearchFlexVendors(input) },
} as const;

export interface FlexCodingSearchOutput<T> {
  items: T[];
  nextToken: string | null;
  total: number;
}

export interface FlexCodingSearchPayload<T> {
  input: FlexCodingSearchInput;
  output: FlexCodingSearchOutput<T>;
  loading: boolean;
  // SEE-1392: the error field `FlexCodingState` used to lack. Per-search rather than
  // one flag on the slice, so a failed cost-code search does not blank the GL-code
  // dropdown. Branch on `hadError`; `error` is best-effort and is null when the
  // rejection carried no Error.
  hadError: boolean;
  error: string | null;
}

export interface FlexCodingState {
  flexLoading: boolean;
  flexCodeImportFile: FlexCodeImport | undefined;
  flexProjects: FlexCode[] | undefined;
  costCodes: FlexCostCode[] | undefined;
  costTypes: FlexCostType[] | undefined;
  flexGlCodes: FlexGlCode[] | undefined;
  vendors: FlexVendor[] | undefined;
  searchGlCodes?: FlexCodingSearchPayload<FlexGlCode>;
  searchProjects?: FlexCodingSearchPayload<FlexCode>;
  searchCostCodes?: FlexCodingSearchPayload<FlexCostCode>;
  searchCostTypes?: FlexCodingSearchPayload<FlexCostType>;
  searchVendors?: FlexCodingSearchPayload<FlexVendor>;
}

export const initialState: FlexCodingState = {
  flexLoading: false,
  flexCodeImportFile: undefined,
  flexProjects: undefined,
  costCodes: undefined,
  costTypes: undefined,
  flexGlCodes: undefined,
  vendors: undefined,
  searchGlCodes: undefined,
  searchProjects: undefined,
  searchCostCodes: undefined,
  searchCostTypes: undefined,
  searchVendors: undefined,
};

export interface FlexCodingSearchInput {
  paidolId: string;
  glCodeId?: string;
  flexCodeId?: string;
  searchTerm: string;
  nextToken?: string;
  retrieve_all?: boolean;
  specificIds?: string[];
  cardGroupId?: string;
  isSettingsRequest?: boolean;
}

export const searchFlexGlCodesDebounced = (props: FlexCodingSearchInput) =>
  createDebouncedThunk(
    (dispatch: AppDispatch) => {
      return dispatch(searchFlexGlCodes(props));
    },
    'searchFlexGlCodes',
    500
  );

export const searchFlexCodesDebounced = (props: FlexCodingSearchInput) =>
  createDebouncedThunk(
    (dispatch: AppDispatch) => {
      return dispatch(searchFlexCodes(props));
    },
    'searchFlexCodes',
    500
  );

export const searchFlexCostCodesDebounced = (props: FlexCodingSearchInput) =>
  createDebouncedThunk(
    (dispatch: AppDispatch) => {
      return dispatch(searchFlexCostCodes(props));
    },
    'searchFlexCostCodes',
    500
  );

export const searchFlexCostTypesDebounced = (props: FlexCodingSearchInput) =>
  createDebouncedThunk(
    (dispatch: AppDispatch) => {
      return dispatch(searchFlexCostTypes(props));
    },
    'searchFlexCostTypes',
    500
  );

export const searchFlexVendorsDebounced = (props: FlexCodingSearchInput) =>
  createDebouncedThunk(
    (dispatch: AppDispatch) => {
      return dispatch(searchFlexVendors(props));
    },
    'searchFlexVendors',
    500
  );

export const getMostRecentFlexCodeImportFile = createAsyncThunk(
  'flexCoding/getMostRecentFlexCodeImportFile',
  async (paidolId: string, { dispatch }) => {
    return (
      graphqlRequest(
        graphqlOperation(flexCodeImportByPaidolId, {
          paidolId,
          sortDirection: 'DESC',
          filter: {
            status: { eq: 'COMPLETED' },
          },
        }),
        dispatch
      ) as Promise<GraphQLResult<FlexCodeImportByPaidolIdQuery>>
    ).then((results) => results?.data?.flexCodeImportByPaidolId?.items?.[0]);
  }
);

export const searchFlexGlCodes = createAsyncThunk(
  'flexCoding/searchFlexGlCodes',
  async ({
    paidolId,
    searchTerm,
    nextToken,
    specificIds,
    cardGroupId,
    isSettingsRequest,
  }: FlexCodingSearchInput) => {
    const variables: { input: SearchFlexCodingInput } = {
      input: {
        limit: isSettingsRequest ? FLEX_SETTINGS_LIMIT : FLEX_PAGE_LIMIT,
        filter: {
          paidolId: { eq: paidolId },
        },
        nextToken,
      },
    };

    if (searchTerm && variables.input.filter) {
      variables.input.filter.searchTerm = searchTerm;
    }

    if (specificIds?.length && variables.input.filter) {
      variables.input.filter.specificIds = specificIds;
    }

    if (cardGroupId && variables.input.filter) {
      variables.input.filter.cardGroupId = cardGroupId;
    }

    if (isSettingsRequest !== undefined && variables.input.filter) {
      variables.input.filter.isSettingsRequest = isSettingsRequest;
    }

    // SEE-301 Phase B: CDK AppSync via transactionService. The CDK payload carries slim
    // mirrors of the flex entities — no `__typename`, no `flexCodeImport` connection — while
    // this slice is typed on the Amplify models, so the cast happens here at the boundary.
    const payload = await transactionService.v2SearchFlexGlCodes(variables.input);
    return payload as FlexCodingSearchOutput<FlexGlCode> | null;
  }
);

export const loadMoreFlexGlCodes = createAsyncThunk(
  'flexCoding/loadMoreFlexGlCodes',
  async ({ paidolId, nextToken, cardGroupId, searchTerm, isSettingsRequest }: FlexCodingSearchInput) => {
    const variables: { input: SearchFlexCodingInput } = {
      input: {
        limit: isSettingsRequest ? FLEX_SETTINGS_LIMIT : FLEX_PAGE_LIMIT,
        filter: { paidolId: { eq: paidolId } },
        nextToken,
      },
    };
    if (searchTerm && variables.input.filter) {
      variables.input.filter.searchTerm = searchTerm;
    }
    if (cardGroupId && variables.input.filter) {
      variables.input.filter.cardGroupId = cardGroupId;
    }
    if (isSettingsRequest !== undefined && variables.input.filter) {
      variables.input.filter.isSettingsRequest = isSettingsRequest;
    }
    const payload = await transactionService.v2SearchFlexGlCodes(variables.input);
    return payload as FlexCodingSearchOutput<FlexGlCode> | null;
  }
);

export const loadMoreFlexCodes = createAsyncThunk(
  'flexCoding/loadMoreFlexCodes',
  async ({
    paidolId,
    glCodeId,
    searchTerm,
    nextToken,
    cardGroupId,
    isSettingsRequest,
  }: FlexCodingSearchInput) => {
    const payload = await transactionService.v2SearchFlexCodes({
      limit: isSettingsRequest ? FLEX_SETTINGS_LIMIT : FLEX_PAGE_LIMIT,
      filter: {
        paidolId: { eq: paidolId },
        glCodeId: { eq: glCodeId },
        searchTerm,
        ...(cardGroupId ? { cardGroupId } : {}),
        ...(isSettingsRequest !== undefined ? { isSettingsRequest } : {}),
      },
      nextToken,
    });
    return payload as FlexCodingSearchOutput<FlexCode> | null;
  }
);

export const searchFlexCodes = createAsyncThunk(
  'flexCoding/searchFlexCodes',
  async ({
    paidolId,
    glCodeId,
    searchTerm,
    nextToken,
    retrieve_all,
    specificIds,
    cardGroupId,
    isSettingsRequest,
  }: FlexCodingSearchInput) => {
    const payload = await transactionService.v2SearchFlexCodes({
      limit: isSettingsRequest ? FLEX_SETTINGS_LIMIT : FLEX_PAGE_LIMIT,
      filter: {
        paidolId: { eq: paidolId },
        glCodeId: { eq: glCodeId },
        searchTerm,
        ...(specificIds?.length ? { specificIds } : {}),
        ...(cardGroupId ? { cardGroupId } : {}),
        ...(isSettingsRequest !== undefined ? { isSettingsRequest } : {}),
      },
      nextToken,
      ...(retrieve_all
        ? {
            retrieve_all,
          }
        : {}),
    });
    return payload as FlexCodingSearchOutput<FlexCode> | null;
  }
);

export const searchFlexCostCodes = createAsyncThunk(
  'flexCoding/searchFlexCostCodes',
  async ({ paidolId, glCodeId, flexCodeId, searchTerm, nextToken, specificIds }: FlexCodingSearchInput) => {
    const payload = await transactionService.v2SearchFlexCostCodes({
      limit: FLEX_PAGE_LIMIT,
      filter: {
        paidolId: { eq: paidolId },
        glCodeId: { eq: glCodeId },
        flexCodeId: { eq: flexCodeId },
        searchTerm,
        ...(specificIds?.length ? { specificIds } : {}),
      },
      nextToken,
    });
    return payload as FlexCodingSearchOutput<FlexCostCode> | null;
  }
);

export const searchFlexCostTypes = createAsyncThunk(
  'flexCoding/searchFlexCostTypes',
  async ({ paidolId, glCodeId, flexCodeId, searchTerm, nextToken, specificIds }: FlexCodingSearchInput) => {
    const payload = await transactionService.v2SearchFlexCostTypes({
      limit: FLEX_PAGE_LIMIT,
      filter: {
        paidolId: { eq: paidolId },
        glCodeId: { eq: glCodeId },
        flexCodeId: { eq: flexCodeId },
        searchTerm,
        ...(specificIds?.length ? { specificIds } : {}),
      },
      nextToken,
    });
    return payload as FlexCodingSearchOutput<FlexCostType> | null;
  }
);

export const searchFlexVendors = createAsyncThunk(
  'flexCoding/searchFlexVendors',
  async ({ paidolId, glCodeId, flexCodeId, searchTerm, nextToken, specificIds }: FlexCodingSearchInput) => {
    const payload = await transactionService.v2SearchFlexVendors({
      limit: FLEX_PAGE_LIMIT,
      filter: {
        paidolId: { eq: paidolId },
        glCodeId: { eq: glCodeId },
        flexCodeId: { eq: flexCodeId },
        searchTerm,
        ...(specificIds?.length ? { specificIds } : {}),
      },
      nextToken,
    });
    return payload as FlexCodingSearchOutput<FlexVendor> | null;
  }
);

export const listFlexCodes = createAsyncThunk(
  'flexCoding/listFlexCodes',
  async (flexCodeImportId: string, { dispatch }) => {
    return (
      graphqlRequest(
        graphqlOperation(flexCodesByFlexCodeImportId, {
          filter: {
            isActive: { eq: true },
          },
          flexCodeImportId,
          limit: 10000,
          sortDirection: 'ASC',
        }),
        dispatch
      ) as Promise<GraphQLResult<FlexCodesByFlexCodeImportIdQuery>>
    ).then((results) => results?.data?.flexCodesByFlexCodeImportId?.items || []);
  }
);

export const listFlexCostCodes = createAsyncThunk(
  'flexCoding/listFlexCostCodes',
  async (flexCodeImportId: string, { dispatch }) => {
    return (
      graphqlRequest(
        graphqlOperation(flexCostCodesByFlexCodeImportId, {
          flexCodeImportId,
          limit: 10000,
          sortDirection: 'ASC',
        }),
        dispatch
      ) as Promise<GraphQLResult<FlexCostCodesByFlexCodeImportIdQuery>>
    ).then((results) => results?.data?.flexCostCodesByFlexCodeImportId?.items || []);
  }
);

export const listFlexCostTypes = createAsyncThunk(
  'flexCoding/listFlexCostTypes',
  async (flexCodeImportId: string, { dispatch }) => {
    return (
      graphqlRequest(
        graphqlOperation(flexCostTypesByFlexCodeImportId, {
          flexCodeImportId,
          limit: 10000,
          sortDirection: 'ASC',
        }),
        dispatch
      ) as Promise<GraphQLResult<FlexCostTypesByFlexCodeImportIdQuery>>
    ).then((results) => results?.data?.flexCostTypesByFlexCodeImportId?.items || []);
  }
);

export const listFlexGlCodes = createAsyncThunk(
  'flexCoding/listFlexGlCodes',
  async (flexCodeImportId: string, { dispatch }) => {
    return (
      graphqlRequest(
        graphqlOperation(flexGlCodeByFlexCodeImportId, {
          flexCodeImportId,
          limit: 10000,
          sortDirection: 'ASC',
        }),
        dispatch
      ) as Promise<GraphQLResult<FlexGlCodeByFlexCodeImportIdQuery>>
    ).then((results) => results?.data?.flexGlCodeByFlexCodeImportId?.items || []);
  }
);

export const listFlexVendors = createAsyncThunk(
  'flexCoding/listFlexVendors',
  async (flexCodeImportId: string, { dispatch }) => {
    return (
      graphqlRequest(
        graphqlOperation(flexVendorsByFlexCodeImportId, {
          flexCodeImportId,
          limit: 10000,
          sortDirection: 'ASC',
        }),
        dispatch
      ) as Promise<GraphQLResult<FlexVendorsByFlexCodeImportIdQuery>>
    ).then((results) => results?.data?.flexVendorsByFlexCodeImportId?.items || []);
  }
);

const flexCodingSlice = createSlice({
  name: 'flexCoding',
  initialState,
  reducers: {
    resetFlexCodingSlice: () => {
      return initialState;
    },
    setGlCodeLoading: (state, action) => {
      if (state.searchGlCodes) {
        state.searchGlCodes.loading = action.payload;
      }
    },
    setFlexCodeLoading: (state, action) => {
      if (state.searchProjects) {
        state.searchProjects.loading = action.payload;
      }
    },
    setCostCodeLoading: (state, action) => {
      if (state.searchCostCodes) {
        state.searchCostCodes.loading = action.payload;
      }
    },
    setCostTypeLoading: (state, action) => {
      if (state.searchCostTypes) {
        state.searchCostTypes.loading = action.payload;
      }
    },
    setVendorLoading: (state, action) => {
      if (state.searchVendors) {
        state.searchVendors.loading = action.payload;
      }
    },
    clearFlexGlCodeSearch: (state) => {
      state.searchGlCodes = undefined;
    },
    clearFlexProjectSearch: (state) => {
      state.searchProjects = undefined;
    },
    clearAllFlexCodingSearches: (state) => {
      state.searchGlCodes = undefined;
      state.searchProjects = undefined;
      state.searchCostCodes = undefined;
      state.searchCostTypes = undefined;
      state.searchVendors = undefined;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(searchFlexGlCodes.pending, (state, action) => {
      state.searchGlCodes = {
        input: state.searchGlCodes?.input || { paidolId: '', searchTerm: '', specificIds: [] },
        output: { items: state.searchGlCodes?.output.items ?? [], nextToken: null, total: 0 },
        loading: true,
        hadError: false,
        error: null,
      };
    });
    builder.addCase(searchFlexGlCodes.fulfilled, (state, action) => {
      state.searchGlCodes = {
        input: action.meta.arg,
        output: {
          items: sortAlphanumericArrays(action.payload?.items || [], 'name'),
          nextToken: action.payload?.nextToken || null,
          total: action.payload?.total ?? 0,
        },
        loading: false,
        hadError: false,
        error: null,
      };
    });
    builder.addCase(loadMoreFlexGlCodes.pending, (state) => {
      if (state.searchGlCodes) {
        state.searchGlCodes.loading = true;
      }
    });
    builder.addCase(loadMoreFlexGlCodes.fulfilled, (state, action) => {
      if (state.searchGlCodes) {
        const merged = sortAlphanumericArrays(
          [...state.searchGlCodes.output.items, ...(action.payload?.items || [])],
          'name'
        );
        state.searchGlCodes = {
          ...state.searchGlCodes,
          output: {
            items: merged,
            nextToken: action.payload?.nextToken || null,
            total: action.payload?.total ?? state.searchGlCodes.output.total,
          },
          loading: false,
          hadError: false,
          error: null,
        };
      }
    });
    builder.addCase(loadMoreFlexGlCodes.rejected, (state, action) => {
      if (state.searchGlCodes) {
        state.searchGlCodes.loading = false;
        state.searchGlCodes.hadError = true;
        state.searchGlCodes.error = action.error?.message ?? null;
      }
    });
    builder.addCase(searchFlexGlCodes.rejected, (state, action) => {
      if (state.searchGlCodes) {
        state.searchGlCodes.loading = false;
        state.searchGlCodes.hadError = true;
        state.searchGlCodes.error = action.error?.message ?? null;
      }
    });
    builder.addCase(searchFlexCodes.pending, (state, action) => {
      state.searchProjects = {
        input: state.searchProjects?.input || { paidolId: '', searchTerm: '', specificIds: [] },
        output: {
          items: state?.searchProjects?.output.items ?? [],
          nextToken: state?.searchProjects?.output.nextToken ?? '',
          total: state?.searchProjects?.output.total ?? 0,
        },
        loading: true,
        hadError: false,
        error: null,
      };
    });
    builder.addCase(searchFlexCodes.fulfilled, (state, action) => {
      state.searchProjects = {
        input: action.meta.arg,
        output: {
          items: sortAlphanumericArrays(action.payload?.items || [], 'projectName'),
          nextToken: action.payload?.nextToken || null,
          total: action.payload?.total ?? 0,
        },
        loading: false,
        hadError: false,
        error: null,
      };
    });
    builder.addCase(loadMoreFlexCodes.pending, (state) => {
      if (state.searchProjects) {
        state.searchProjects.loading = true;
      }
    });
    builder.addCase(loadMoreFlexCodes.fulfilled, (state, action) => {
      if (state.searchProjects) {
        state.searchProjects = {
          ...state.searchProjects,
          output: {
            items: [...state.searchProjects.output.items, ...(action.payload?.items || [])],
            nextToken: action.payload?.nextToken || null,
            total: action.payload?.total ?? state.searchProjects.output.total,
          },
          loading: false,
          hadError: false,
          error: null,
        };
      }
    });
    builder.addCase(loadMoreFlexCodes.rejected, (state, action) => {
      if (state.searchProjects) {
        state.searchProjects.loading = false;
        state.searchProjects.hadError = true;
        state.searchProjects.error = action.error?.message ?? null;
      }
    });
    builder.addCase(searchFlexCodes.rejected, (state, action) => {
      if (state.searchProjects) {
        state.searchProjects.loading = false;
        state.searchProjects.hadError = true;
        state.searchProjects.error = action.error?.message ?? null;
      }
    });
    builder.addCase(searchFlexCostCodes.pending, (state, action) => {
      state.searchCostCodes = {
        input: state.searchCostCodes?.input || { paidolId: '', searchTerm: '', specificIds: [] },
        output: { items: [], nextToken: null, total: 0 },
        loading: true,
        hadError: false,
        error: null,
      };
    });
    builder.addCase(searchFlexCostCodes.fulfilled, (state, action) => {
      state.searchCostCodes = {
        input: action.meta.arg,
        output: {
          items: sortAlphanumericArrays(action.payload?.items || [], 'code'),
          nextToken: action.payload?.nextToken || null,
          total: action.payload?.total ?? 0,
        },
        loading: false,
        hadError: false,
        error: null,
      };
    });
    // SEE-1392: `.pending` above sets `loading: true` and wipes `output`. Without this the
    // flag is never cleared — not by anything short of a later successful search,
    // `clearAllFlexCodingSearches` or logout — so the dropdown spins forever. Reachable as
    // of the SEE-301 repoint: the Amplify dispatcher returned handler failures as HTTP 200
    // data, so this thunk never rejected; the CDK dispatcher raises.
    //
    // Clearing the flag stops the spinner; `hadError` is what tells the user the search
    // FAILED rather than showing them an empty list. The error field this comment used to
    // say `FlexCodingState` lacked now exists on each search payload.
    builder.addCase(searchFlexCostCodes.rejected, (state, action) => {
      if (state.searchCostCodes) {
        state.searchCostCodes.loading = false;
        state.searchCostCodes.hadError = true;
        state.searchCostCodes.error = action.error?.message ?? null;
      }
    });
    builder.addCase(searchFlexCostTypes.pending, (state, action) => {
      state.searchCostTypes = {
        input: state.searchCostTypes?.input || { paidolId: '', searchTerm: '', specificIds: [] },
        output: { items: [], nextToken: null, total: 0 },
        loading: true,
        hadError: false,
        error: null,
      };
    });
    builder.addCase(searchFlexCostTypes.fulfilled, (state, action) => {
      state.searchCostTypes = {
        input: action.meta.arg,
        output: {
          items: sortAlphanumericArrays(action.payload?.items || [], 'name'),
          nextToken: action.payload?.nextToken || null,
          total: action.payload?.total ?? 0,
        },
        loading: false,
        hadError: false,
        error: null,
      };
    });
    builder.addCase(searchFlexCostTypes.rejected, (state, action) => {
      if (state.searchCostTypes) {
        state.searchCostTypes.loading = false;
        state.searchCostTypes.hadError = true;
        state.searchCostTypes.error = action.error?.message ?? null;
      }
    });
    builder.addCase(searchFlexVendors.pending, (state, action) => {
      state.searchVendors = {
        input: state.searchVendors?.input || { paidolId: '', searchTerm: '', specificIds: [] },
        output: { items: [], nextToken: null, total: 0 },
        loading: true,
        hadError: false,
        error: null,
      };
    });
    builder.addCase(searchFlexVendors.fulfilled, (state, action) => {
      state.searchVendors = {
        input: action.meta.arg,
        output: {
          items: sortAlphanumericArrays(action.payload?.items || [], 'name'),
          nextToken: action.payload?.nextToken || null,
          total: action.payload?.total ?? 0,
        },
        loading: false,
        hadError: false,
        error: null,
      };
    });
    // SEE-1392: this one already wiped `output` on rejection, which made a denial
    // pixel-identical to a `.fulfilled` with no matches — only `input` differed. The
    // wipe stays; what changes is that the failure is now recorded, so the consumer
    // renders an error instead of an empty ERP.
    builder.addCase(searchFlexVendors.rejected, (state, action) => {
      state.searchVendors = {
        input: state.searchVendors?.input || { paidolId: '', searchTerm: '', specificIds: [] },
        output: { items: [], nextToken: null, total: 0 },
        loading: false,
        hadError: true,
        error: action.error?.message ?? null,
      };
    });
    builder.addCase(getMostRecentFlexCodeImportFile.fulfilled, (state, action) => {
      state.flexLoading = false;
      state.flexCodeImportFile = action.payload as FlexCodeImport;
    });
    builder.addCase(listFlexCodes.fulfilled, (state, action) => {
      state.flexLoading = false;
      state.flexProjects = action.payload as FlexCode[];
    });
    builder.addCase(listFlexCostCodes.fulfilled, (state, action) => {
      state.flexLoading = false;
      state.costCodes = action.payload as FlexCostCode[];
    });
    builder.addCase(listFlexCostTypes.fulfilled, (state, action) => {
      state.flexLoading = false;
      state.costTypes = action.payload as FlexCostType[];
    });
    builder.addCase(listFlexGlCodes.fulfilled, (state, action) => {
      state.flexLoading = false;
      state.flexGlCodes = action.payload as FlexGlCode[];
    });
    builder.addCase(listFlexVendors.fulfilled, (state, action) => {
      state.flexLoading = false;
      state.vendors = action.payload as FlexVendor[];
    });
    builder.addMatcher(
      isAnyOf(
        listFlexCodes.pending,
        listFlexCostCodes.pending,
        listFlexCostTypes.pending,
        listFlexGlCodes.pending,
        listFlexVendors.pending,
        getMostRecentFlexCodeImportFile.pending
      ),
      (state) => {
        state.flexLoading = true;
      }
    );
    builder.addMatcher(
      isAnyOf(
        listFlexCodes.rejected,
        listFlexCostCodes.rejected,
        listFlexCostTypes.rejected,
        listFlexGlCodes.rejected,
        listFlexVendors.rejected,
        getMostRecentFlexCodeImportFile.rejected
      ),
      (state) => {
        state.flexLoading = false;
      }
    );
  },
});

export const {
  resetFlexCodingSlice,
  setGlCodeLoading,
  setFlexCodeLoading,
  setCostCodeLoading,
  setCostTypeLoading,
  setVendorLoading,
  clearFlexGlCodeSearch,
  clearFlexProjectSearch,
  clearAllFlexCodingSearches,
} = flexCodingSlice.actions;

export const selectFlexCodingSlice = (state: RootState): FlexCodingState => state.flexCoding || initialState;

export default flexCodingSlice.reducer;
